import { gql } from "@apollo/client";
import {
    createPagePagingActions,
    SortDirection,
    Table as AdminTable,
    TableQuery,
    useTableQuery,
    useTableQueryPaging,
    useTableQuerySort,
    VisibleType,
} from "@comet/admin";
import { ContentWrapper, formatDateTimeAsString, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { type GQLUserAdminQuery, type GQLUserLicencesAdminQuery, type GQLUserLicencesAdminQueryVariables } from "@src/graphql.generated";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { useIntl } from "react-intl";

const userLicencesQuery = gql`
    query UserLicencesAdmin($userId: ID!, $page: Int, $size: Int, $sort: SortInput) {
        userLicencesAdmin(userId: $userId, page: $page, size: $size, sort: $sort) {
            nodes {
                id
                activatedAt
                code
                package {
                    name
                }
                camera {
                    cameraId
                }
                activeFrom
                activeTill
            }
            totalCount
            totalPages
            nextPage
            previousPage
        }
    }
`;

const LicencesTable: FC<PropsWithChildren<GQLUserAdminQuery>> = ({ userAdmin }) => {
    const intl = useIntl();
    const pagingApi = useTableQueryPaging(1);
    const sortApi = useTableQuerySort({ columnName: "licence.activatedAt", direction: SortDirection.DESC });
    const addAdditionalQueryVariablesFn = useCallback(() => ({ userId: userAdmin.id, size: 5 }), [userAdmin.id]);

    const memoizedVariables = useMemoizedTableVariables<GQLUserLicencesAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLUserLicencesAdminQuery, GQLUserLicencesAdminQueryVariables>()(userLicencesQuery, {
        resolveTableData: ({ userLicencesAdmin }) => ({
            data: userLicencesAdmin.nodes,
            totalCount: userLicencesAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, userLicencesAdmin),
        }),
        variables: memoizedVariables,
    });

    const fileName = intl.formatMessage(
        {
            id: "reviermanager.common.export.userPurchases",
            defaultMessage: "Käufe_{firstname}_{lastname}",
        },
        { firstname: userAdmin.firstname, lastname: userAdmin.lastname },
    );

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    return (
        <ContentWrapper title={intl.formatMessage({ id: "reviermanager.user.purchases", defaultMessage: "Käufe" })}>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        exportApis={[exportApi]}
                        {...tableData}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.user.purchases",
                                defaultMessage: "{count, plural, one {Kauf} other {Käufe}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        sortApi={sortApi}
                        columns={[
                            {
                                name: "camera.cameraId",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.user.licence.camera.id", defaultMessage: "Gerät-ID" }),
                                render: ({ camera }) => camera && camera.cameraId,
                            },
                            {
                                name: "package.name",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.user.licence.package.name", defaultMessage: "Paketname" }),
                                render: ({ package: { name } }) => name,
                            },
                            {
                                name: "id",
                                header: intl.formatMessage({ id: "reviermanager.common.purchases.id", defaultMessage: "UUID" }),
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                            },
                            {
                                name: "code",
                                header: intl.formatMessage({
                                    id: "reviermanager.common.purchases.code",
                                    defaultMessage: "Code",
                                }),
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                            },
                            {
                                name: "licence.activatedAt",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.camera.table.activatedAt", defaultMessage: "aktiviert am" }),
                                render: ({ activatedAt }) => activatedAt && <FormattedDateWidthDateTime value={activatedAt} />,
                                renderExcel: ({ activatedAt }) => formatDateTimeAsString(activatedAt, intl),
                            },
                            {
                                name: "activeFrom",
                                header: intl.formatMessage({
                                    id: "reviermanager.common.purchases.activeFrom",
                                    defaultMessage: "Lizenz aktiviert am",
                                }),
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                                renderExcel: ({ activeFrom }) => formatDateTimeAsString(activeFrom, intl),
                            },
                            {
                                name: "activeTill",
                                visible: { [VisibleType.Browser]: false, [VisibleType.Export]: true },
                                header: intl.formatMessage({ id: "reviermanager.common.purchases.activeTill", defaultMessage: "Lizenz läuft ab am" }),
                                renderExcel: ({ activeTill }) => formatDateTimeAsString(activeTill, intl),
                            },
                        ]}
                    />
                )}
                {tableData && <CSVExportButton exportApi={exportApi} />}
            </TableQuery>
        </ContentWrapper>
    );
};

export default LicencesTable;
